import { atom, Atom } from "jotai";
import { AccountHintType } from "@/entities/DataAssociation/Draft.model";
import { accountsAtom } from "./account.state";
import { tableRowsAtom } from "./table.state";

export const isNameInUseAtom: Atom<
    (name: string) => [boolean, string[], string[]]
> = atom((get) => {
    const accounts = get(accountsAtom);
    const tableRows = get(tableRowsAtom);
    const existingAccounts = Object.values(accounts).filter(
        (account) => account.type === AccountHintType.EXISTING,
    );
    const fn = (name: string): [boolean, string[], string[]] => {
        const rowsWithName = tableRows
            .filter(
                (row) =>
                    row.userConfirmed &&
                    row.account?.accountName === name &&
                    row.account?.type === AccountHintType.NEW &&
                    !/excluded|reported/.test(row.account?.label || ""),
            )
            .map((row) => row.rowIndex);

        const existingAccountMatches = existingAccounts.filter(
            (account) => account.name === name,
        );
        return [
            existingAccountMatches.length > 0 || rowsWithName.length > 0,
            rowsWithName,
            existingAccountMatches.map((account) => account.id),
        ];
    };
    return fn;
});

export const accountNamingErrorMessageAtom: Atom<
    (
        name: string,
        ignore?: { currentRowIndex?: string; currentAccountId?: string },
    ) => string | null
> = atom((get) => {
    const isNameInUse = get(isNameInUseAtom);
    const fn = (
        name: string,
        ignore?: { currentRowIndex?: string; currentAccountId?: string },
    ): string | null => {
        const [isInUse, rowsInUse, existingAccountIds] = isNameInUse(name);
        if (!isInUse) return null;
        if (
            isInUse &&
            existingAccountIds.filter((id) => id !== ignore?.currentAccountId)
                .length > 0
        )
            return "Account name already used in an existing account.";

        const filteredRows = rowsInUse.filter(
            (row) => row !== ignore?.currentRowIndex,
        );
        if (filteredRows.length > 0)
            return `Account name already in use in row${filteredRows.length !== 1 ? "s" : ""} ${filteredRows.join(", ")}`;
        return null;
    };
    return fn;
});
